'use client'

import { useCallback, useRef, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useConversationNavigator } from '@marketplace-web/messaging/navigation-feature'

import {
  ComplaintResolutionAction,
  ComplaintResolutionChoice,
  ComplaintModel,
  transformComplaintDto,
  getSnadComplaint,
} from '@marketplace-web/complaints/complaints-data'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'

import GenericErrorModal from '../GenericErrorModal'
import RequestReturn, { RequestReturnRef } from '../RequestReturn'
import RequestReturnDetailsProvider from '../RequestReturn/RequestReturnProvider'
import ResolveIssueButton from '../ResolveIssueButton'
import ChooseComplaintResolutionDialog from './ChooseComplaintResolutionDialog'
import PartialRefundOfferDialog from './PartialRefundOfferDialog'

enum ChooseResolutionActiveDialog {
  ChooseResolutionDialog,
  OfferPartialRefundDialog,
  FullRefundDialog,
  RequestToReturnDialog,
  ErrorDialog,
  None,
}

type OwnProps = {
  complaint?: ComplaintModel | null
  transactionId?: string | null
}

type Props = Partial<ConversationActionProps> & OwnProps

const ChooseComplaintResolution = ({
  primary,
  title,
  inverse,
  theme,
  size,
  complaint: preFetchedComplaint,
  transactionId,
}: Props) => {
  const requestReturnRef = useRef<RequestReturnRef>(null)

  const [activeDialog, setActiveDialog] = useState(ChooseResolutionActiveDialog.None)

  const conversationNavigator = useConversationNavigator()
  const navigationStartMark = useRef<
    ReturnType<(typeof conversationNavigator)['createNavigationStartMark']> | undefined
  >(undefined)

  const setConversationNavigationStartMark = useCallback(() => {
    navigationStartMark.current = conversationNavigator.createNavigationStartMark()
  }, [conversationNavigator])

  const storeConversationNavigationStartMark = useCallback(() => {
    navigationStartMark.current?.store()
  }, [])

  const messageThreadUrl = preFetchedComplaint?.userMsgThreadId
    ? conversationNavigator.createUrl(preFetchedComplaint?.userMsgThreadId)
    : undefined

  const {
    fetch: fetchSnadComplaint,
    transformedData: fetchedComplaint,
    isLoading: isComplaintLoading,
  } = useFetch(getSnadComplaint, transformComplaintDto)

  const fetchComplaint = async () => {
    if (preFetchedComplaint || !transactionId) return

    const response = await fetchSnadComplaint(Number(transactionId))

    if ('errors' in response) {
      setActiveDialog(ChooseResolutionActiveDialog.ErrorDialog)
    }
  }

  const complaint = preFetchedComplaint || fetchedComplaint
  const complaintResolutionActions = complaint?.resolutions?.find(
    resolutions => resolutions.action === ComplaintResolutionAction.ChooseResolution,
  )?.resolutions

  const handleDialogClose = () => {
    setActiveDialog(ChooseResolutionActiveDialog.None)
  }

  const handleErrorDialogClose = () => {
    handleDialogClose()

    if (preFetchedComplaint?.userMsgThreadId) {
      conversationNavigator.navigate(preFetchedComplaint.userMsgThreadId)

      return
    }

    reloadPage()
  }

  const handleChooseResolutionDialogOpen = () => {
    setActiveDialog(ChooseResolutionActiveDialog.ChooseResolutionDialog)
  }

  const clickChooseResolutionButton = () => {
    fetchComplaint()
    handleChooseResolutionDialogOpen()
  }

  const openDialog = (resolution: ComplaintResolutionChoice) => {
    switch (resolution) {
      case ComplaintResolutionChoice.OfferPartialRefund:
        setActiveDialog(ChooseResolutionActiveDialog.OfferPartialRefundDialog)
        break
      case ComplaintResolutionChoice.Refund:
        setActiveDialog(ChooseResolutionActiveDialog.FullRefundDialog)
        break
      case ComplaintResolutionChoice.RequestToReturn:
        setActiveDialog(ChooseResolutionActiveDialog.RequestToReturnDialog)
        if (requestReturnRef.current) {
          requestReturnRef.current.handleReturnModalOpen()
        }
        break
      default:
        break
    }
  }

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size || 'medium'}
        theme={theme}
        inverse={inverse}
        onClick={clickChooseResolutionButton}
        testId="choose-complaint-resolution-conversation-action"
      />
      <ChooseComplaintResolutionDialog
        isOpen={activeDialog === ChooseResolutionActiveDialog.ChooseResolutionDialog}
        transactionId={complaint?.transactionId?.toString()}
        complaintId={complaint?.id?.toString()}
        complaintResolutionActions={complaintResolutionActions}
        isComplaintLoading={isComplaintLoading}
        onDialogOpen={openDialog}
        onClose={handleDialogClose}
      />
      <PartialRefundOfferDialog
        isOpen={activeDialog === ChooseResolutionActiveDialog.OfferPartialRefundDialog}
        url={messageThreadUrl}
        complaint={complaint}
        onSubmitError={() => setActiveDialog(ChooseResolutionActiveDialog.ErrorDialog)}
        onSubmit={setConversationNavigationStartMark}
        onUrlNavigation={storeConversationNavigationStartMark}
        onClose={handleDialogClose}
      />
      <RequestReturnDetailsProvider>
        <RequestReturn
          ref={requestReturnRef}
          isChooseResolutionFlow
          complaint={complaint}
          url={messageThreadUrl}
          onConfirm={setConversationNavigationStartMark}
          onUrlNavigation={storeConversationNavigationStartMark}
          conversationTransactionId={Number(transactionId)}
        />
      </RequestReturnDetailsProvider>
      <ResolveIssueButton
        isChooseResolutionFlow
        complaint={complaint}
        url={messageThreadUrl}
        onModalSubmit={setConversationNavigationStartMark}
        onUrlNavigation={storeConversationNavigationStartMark}
        shouldOpen={activeDialog === ChooseResolutionActiveDialog.FullRefundDialog}
      />
      <GenericErrorModal
        isShown={activeDialog === ChooseResolutionActiveDialog.ErrorDialog}
        onClose={handleErrorDialogClose}
      />
    </>
  )
}

export default ChooseComplaintResolution
